import React, { useMemo, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Tooltip, Box } from "@mui/material";
import IncidentTableHead from "./IncidentTableHead";
import dayjs from "dayjs";
import heartLoading from "../../assests/heartLoading.gif";

const IncidentTable = ({
  loading,
  incidents,
  selectedParentDomain,
  selectedDomain,
  selectedSubDomain,
  selectedProduct,
  searchValue,
  selectedCategory,
  resetAll,
  totalCount,
  loadMore,
  hasMore,
  isFetchingMore,
  sortColumn,
  sortOrder,
  handleSort,
  columnSearch,
  onColumnSearchChange,
  setResolutionPendingStatus,
}) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isFetchingMore) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, loadMore]);
  // Sort incidents (filtering is now done server-side via API)
  const filteredAndSortedIncidents = useMemo(() => {
    let filtered = [...incidents];

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortColumn) {
          case "snowId":
            aValue = (a.snowId || "").toLowerCase();
            bValue = (b.snowId || "").toLowerCase();
            break;
          case "incidentDescription":
            aValue = (a.incidentDescription || "").toLowerCase();
            bValue = (b.incidentDescription || "").toLowerCase();
            break;
          case "incidentCategory":
            aValue = (a.displayIncidentCategory || a.incidentCategory || "").toLowerCase();
            bValue = (b.displayIncidentCategory || b.incidentCategory || "").toLowerCase();
            break;
          case "assignedTo":
            aValue = (a.assignedTo || "").toLowerCase();
            bValue = (b.assignedTo || "").toLowerCase();
            break;
          case "status":
            aValue = (a.status || "").toLowerCase();
            bValue = (b.status || "").toLowerCase();
            break;
          case "resolutionPendingStatus":
            // Extract numeric value for proper sorting
            aValue = parseInt((a.resolutionPendingStatus || "0").replace(/\D/g, "")) || 0;
            bValue = parseInt((b.resolutionPendingStatus || "0").replace(/\D/g, "")) || 0;
            break;
          case "createdAt": // Support both for backward compatibility
            // Sort by date timestamp
            aValue = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
            bValue = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [incidents, sortColumn, sortOrder]);

  const displayIncidents = filteredAndSortedIncidents;
  return (
    <div className="bg-white overflow-hidden">
      {!loading && displayIncidents.length > 0 && (
        <div className="px-4 py-2 text-[13px] text-gray-500">
          {displayIncidents.length} of {totalCount} results loaded
        </div>
      )}
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 300px)" }}>
        <table className="w-full">
          <IncidentTableHead
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            handleSort={handleSort}
            columnSearch={columnSearch}
            onColumnSearchChange={onColumnSearchChange}
            setResolutionPendingStatus={setResolutionPendingStatus}
          />
          <tbody className="bg-[#eee] divide-y divide-gray-200">
            {loading ? (
              // Skeleton Loading
              [...Array(5)].map((_, index) => (
                <tr key={index} className="animate-pulse">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4" style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mt-2"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                </tr>
              ))
            ) : displayIncidents.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-20">
                  <div className="flex flex-col items-center justify-center text-center">
                    <svg
                      className="w-24 h-24 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      No Incidents Found
                    </h3>
                    <p className="text-gray-500 max-w-md">
                      {selectedParentDomain || selectedDomain || selectedSubDomain || selectedProduct || searchValue || selectedCategory
                        ? "No incidents match the selected filters. Try adjusting your search criteria."
                        : "There are no incidents to display at the moment."}
                    </p>
                    {(selectedParentDomain || selectedDomain || selectedSubDomain || selectedProduct || searchValue || selectedCategory) && (
                      <button
                        onClick={resetAll}
                        className="mt-4 px-4 py-2 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg hover:bg-blue-800"
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              displayIncidents.map((incident, index) => (
                <tr 
                  key={index} 
                  className="hover:bg-[#CCFBF1] transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="text-blue-600 font-medium hover:underline cursor-pointer"
                      onClick={() => {
                        if (incident.recordLink) {
                          window.open(incident.recordLink, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      title={incident.recordLink ? "Click to view incident details" : "No record link available"}
                    >
                      {incident.snowId}
                    </span>
                  </td>
                  <td
                    className="px-6 py-4 text-sm text-gray-900"
                    style={{ width: "300px", minWidth: "300px", maxWidth: "300px" }}
                  >
                    <Tooltip
                      arrow
                      placement="top-start"
                      disableHoverListener={!incident.incidentDescription}
                      enterDelay={300}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "#616161",
                            color: "#ffffff",
                            boxShadow: "0px 6px 16px rgba(15,23,42,0.15)",
                            borderRadius: "8px",
                            maxWidth: 420,
                            maxHeight: 320,
                            overflowY: "auto",
                            p: 1.5,
                            fontSize: "12px",
                            "& .markdown-tooltip *": {
                              color: "#ffffff",
                              fontFamily: "Poppins, sans-serif",
                              margin: 0,
                              fontWeight: 400,
                            },
                            "& .markdown-tooltip strong": {
                              fontWeight: 600,
                            },
                          },
                        },
                        arrow: {
                          sx: { color: "#616161" },
                        },
                      }}
                      title={
                        incident.incidentDescription ? (
                          <Box className="markdown-tooltip">
                            <ReactMarkdown>{incident.incidentDescription}</ReactMarkdown>
                          </Box>
                        ) : (
                          ""
                        )
                      }
                    >
                      <div
                        className="break-words overflow-hidden prose prose-sm max-w-none"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        <ReactMarkdown>{incident.incidentDescription || ""}</ReactMarkdown>
                      </div>
                    </Tooltip>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {incident.displayIncidentCategory || incident.incidentCategory}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {incident.assignedTo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium border-none rounded-sm ${
                      incident.status === 'Complete' ? 'bg-green-100 text-green-800' :
                      incident.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                      incident.status === 'New' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {incident.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`${
                      incident.resolutionPendingStatus === '0 days' ? 'text-green-600' :
                      parseInt(incident.resolutionPendingStatus) <= 2 ? 'text-yellow-600' :
                      'text-red-600'
                    } font-medium`}>
                      {incident.resolutionPendingStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {incident.createdAt ? dayjs(incident.createdAt).format("MMM D, YYYY") : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div ref={sentinelRef} style={{ height: 1 }} />
      {isFetchingMore && (
        <div className="flex items-center justify-center py-4 gap-2">
          <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
          <span className="text-gray-500 text-sm">Loading more...</span>
        </div>
      )}
      {!hasMore && displayIncidents.length > 0 && !loading && (
        <div className="text-center py-3 text-gray-400 text-sm">
          All {totalCount} incidents loaded
        </div>
      )}
    </div>
  );
};

export default IncidentTable;


